import './installment-line.scss'
import {observer} from "mobx-react";
import PlanInstallmentEditorStore from "../../../stores/plan-installment-editor-store";
import {ButtonIcon} from "../../../../../components/inputs/button-icon/button-icon";
import IconDiscard from "../../../../../components/icons/icon-discard/icon-discard";
import IconApply from "../../../../../components/icons/icon-apply/icon-apply";
import IconDelete from "../../../../../components/icons/icon-delete/icon-delete";
import IconEdit from "../../../../../components/icons/icon-edit/icon-edit";
import {Input} from "../../../../../components/inputs/input/input";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import React from "react";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import {Tooltip} from "../../../../../components/tooltip/tooltip";
import IconInfo from "../../../../../components/icons/icon-info/icon-info";
import Icon from "../../../../../components/icons/icon/icon";
import {FONT_COLOR, FONT_VARIANT} from "../../../../../components/fonts/font-props";
import DateRangePickerV2, {DatePickerVariant} from "../../../../../components/date-range-picker-v2/date-range-picker-v2";

interface IIntervalProps {
    installmentStore: PlanInstallmentEditorStore;
}

const Interval: React.FC<IIntervalProps> = observer(({ installmentStore }) => {
    const isIntervalInputDisabled = !installmentStore.isInEditMode() || installmentStore.isImmediate;

    const daysInterval = () => (
        <div className={"installment-line-days-wrapper"}>
            <FontBody2> <FontBody2 variant={FONT_VARIANT.BOLD}>Days</FontBody2> since Last Installment</FontBody2>
            <Tooltip content={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{'Specify how long after the last installment this amount will be charged.'}</FontBody2>}>
                <Icon Component={IconInfo}/>
            </Tooltip>
            <Input
                value={installmentStore.daysSinceLastInstallment}
                onChange={(v) => installmentStore.setDaysSinceLastInstalment(v)}
                disabled={isIntervalInputDisabled}
                type={"number"}
            />
        </div>
    ); 

    const monthsInterval = () => (
        <div className={"installment-line-days-wrapper"}>
            <FontBody2> <FontBody2 variant={FONT_VARIANT.BOLD}>Months</FontBody2> since Last Installment</FontBody2>
            <Tooltip content={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{'Specify how long after the last installment this amount will be charged.'}</FontBody2>}>
                <Icon Component={IconInfo}/>
            </Tooltip>
            <Input
                value={installmentStore.monthsSinceLastInstallment}
                onChange={(v) => installmentStore.setMonthsSinceLastInstallment(v)}
                disabled={isIntervalInputDisabled}
                type={"number"}
            />
        </div>
    );

    const absoluteInterval = () => (
        <div className={"installment-line-days-wrapper"}>
            <FontBody2> <FontBody2 variant={FONT_VARIANT.BOLD}>Specific date</FontBody2> for installment charge</FontBody2>
            <Tooltip content={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{'Specify how long after the last installment this amount will be charged.'}</FontBody2>}>
                <Icon Component={IconInfo}/>
            </Tooltip>
            <DateRangePickerV2
                value={installmentStore.absoluteChargeDateISO}
                onChange={(v) => installmentStore.setInstallmentAbsoluteChargeDate(v)}
                disabled={isIntervalInputDisabled}
                variant={DatePickerVariant.DATE}
                timeZone="UTC"
            />
        </div>
    );

    if(installmentStore.isMonthsInterval()) {
        return monthsInterval();
    }

    if(installmentStore.isAbsoluteInterval()) {
        return absoluteInterval();
    }

    return daysInterval();
});


interface IProps {
    installmentStore: PlanInstallmentEditorStore;
    index: number;
}

const InstallmentLine: React.FC<IProps> = observer(({installmentStore, index}) => {

    return (
        <div className={'installment-line-wrapper'}>
            <div className={"installment-line-index-wrapper"}>
                {index + 1}.
            </div>
            <div className={"installment-line-amount-wrapper"}>
                <FontBody2>
                    {'Amount'}
                </FontBody2>
                <Input
                    adornment={<FontBody1>$</FontBody1>}
                    type={"number"}
                    value={installmentStore.installmentAmount}
                    onChange={(v) => installmentStore.setInstallmentAmount(v)}
                    disabled={!installmentStore.isInEditMode()}
                />
            </div>

            <Interval installmentStore={installmentStore} />

            <div className={"installment-line-buttons"}>
                {installmentStore.isInEditMode()
                    ? <>
                        <ButtonIcon Component={() => <IconDiscard color={`var(--color-accent-3)`}/>}
                                    onClick={() => installmentStore.discardUncommittedChanges()}
                                    disabled={!installmentStore.hasChanges()}
                        />
                        <ButtonIcon Component={() => <IconApply color={`var(--color-primary-1)`}/>}
                                    onClick={() => installmentStore.commitChanges()}
                        />
                    </>
                    : <>
                        <ButtonIcon Component={() => <IconDelete color={`var(--color-accent-1)`}/>}
                                    onClick={() => installmentStore.getSubscriptionPlanEditorStore().installmentsList.removeInstallment(installmentStore)}
                        />
                        <ButtonIcon Component={() => <IconEdit color={`var(--color-accent-1)`}/>}
                                    onClick={() => installmentStore.setInEditMode(true)}
                        />
                    </>
                }
            </div>
        </div>
    );
});

export default InstallmentLine;
